import { FC, useMemo, useCallback, useState, ChangeEvent } from 'react';
import { Loading, Section, Input, Button, Table, ReportCard } from '../../../../components';
import { formataPlaca, moneyFormatter } from '../../../../utils';
import { useToastStore } from '../../../../stores';
import {
	getAllPayment,
	TAnuncioPagamento
} from '../../../../services';
import { Line } from 'react-chartjs-2';
import {
	Form,
	FullLine,
	HalfLine,
	HalfLineButton,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

const AdminPaymentsReport: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [filteredOffers, setFilteredOffers] = useState<TAnuncioPagamento[]>([]);
	const [placa, setPlaca] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [startPaymentDate, setStartPaymentDate] = useState('');
	const [endPaymentDate, setEndPaymentDate] = useState('');
	const [graphLineData, setGraphLineData] = useState<IGraphLineData[]>([]);
	const [clickGenerate, setClickGenerate] = useState(false);

	const handleSetPlaca = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { value } = e.target;
		const newPlaca = formataPlaca(value);
		setPlaca(newPlaca)
	};

	const fetchData = async () => {
		setLoading(true);
		let body = {};
		if (startDate && endDate && endDate >= startDate) {
			body = {
				dataCadastroInicial: `${startDate}T00:00:00.000Z`,
				dataCadastroFinal: `${endDate}T23:59:59.999Z`,
			};
		}
		const data = await getAllPayment(body);
		if (!data) {
			setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
			setLoading(false);
			return;
		}

		const filtered = data.filter((payment) => {
			const isWithinSellDateRange =
				(!startPaymentDate || new Date(payment.dataPagamento!) >= new Date(`${startPaymentDate}T00:00:00.000Z`)) &&
				(!endPaymentDate || new Date(payment.dataPagamento!) <= new Date(`${endPaymentDate}T23:59:59.999Z`));

			const matchesPlate = !placa || payment.anuncio!.placa.includes(placa);

			return isWithinSellDateRange && matchesPlate;
		});

		const salesCountByMonthYear: IGraphLineData[] = [];
		filtered.forEach((offer) => {
			const dataPagamento = new Date(offer.dataPagamento!);
			const label = `${dataPagamento.getMonth() + 1}/${dataPagamento.getFullYear()}`;
			const existingEntry = salesCountByMonthYear.find((entry) => entry.label === label);

			if (existingEntry) {
				existingEntry.qtde++;
			} else {
				salesCountByMonthYear.push({ label, qtde: 1 });
			}
		});

		setGraphLineData(salesCountByMonthYear);
		setFilteredOffers(filtered);
		setClickGenerate(true);
		setLoading(false);
	};

	const tableColumns = useMemo(
		() => [

			{ id: 'registerDate', label: 'Data de Cobrança', width: '100px' },
			{ id: 'plate', label: 'Placa' },
			{ id: 'dealer', label: 'Anunciante' },
			{ id: 'buyer', label: 'Comprador' },
			{ id: 'paymentDate', label: 'Data de Pagamento', width: '100px' },
			{ id: 'value', label: 'Valor Cobrado' },
			{ id: 'valuePaid', label: 'Valor Pago' },
		],
		[]
	);

	const rowMapper = useCallback(
		(payment: TAnuncioPagamento) => ({
			plate: {
				value: payment.anuncio?.placa,
				rawValue: payment.anuncio?.placa,
			},
			dealer: {
				value: payment.anuncio?.concessionaria.fantasia,
				rawValue: payment.anuncio?.concessionaria.fantasia,
			},
			buyer: {
				value: payment.revenda.fantasia,
				rawValue: payment.revenda.fantasia,
			},
			registerDate: {
				value: new Date(payment.dataCadastro!).toLocaleDateString('pt-BR'),
				rawValue: new Date(payment.dataCadastro!).getTime(),
			},
			paymentDate: {
				value: payment.dataPagamento ? new Date(payment.dataPagamento).toLocaleDateString('pt-BR') : '',
				rawValue: payment.dataPagamento ? new Date(payment.dataPagamento).getTime() : '',
			},
			value: {
				value: moneyFormatter(payment.valorCobrado),
				rawValue: payment.valorCobrado,
			},
			valuePaid: {
				value: moneyFormatter(payment.valorPagamento || 0),
				rawValue: payment.valorPagamento || 0,
			},
		}),
		[]
	);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredOffers?.map(rowMapper)} paginate exportToPDF exportToCSV />,
		[filteredOffers, rowMapper, tableColumns]
	);

	const pagamentosRecebidos = filteredOffers.filter(p => !!p.dataPagamento);
	const pagamentosPendentes = filteredOffers.filter(p => !p.dataPagamento);

	const valorRecebido = pagamentosRecebidos.reduce((total, curr) => total + (curr.valorCobrado || 0), 0);
	const valorPendente = pagamentosPendentes.reduce((total, curr) => total + (curr.valorCobrado || 0), 0);

	return (
		<Section>
			<TitleContainer>Relatório de Pagamentos</TitleContainer>
			<Separator />
			<Form>
				<HalfLine>
					<Input
						label="Data de Cobrança Inicial"
						type="date"
						variant='text'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Cobrança Final"
						type="date"
						variant='text'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						min={startDate}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Placa"
						placeholder="Placa"
						type="text"
						variant='text'
						value={placa}
						onChange={handleSetPlaca}
						maxLength={8}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Pagamento Inicial"
						type="date"
						variant='text'
						value={startPaymentDate}
						onChange={(e) => setStartPaymentDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Pagamento Final"
						type="date"
						variant='text'
						value={endPaymentDate}
						onChange={(e) => setEndPaymentDate(e.target.value)}
						min={startPaymentDate}
					/>
				</HalfLine>
				<HalfLineButton>
					<FullLine>
						<HalfLineButton>
							<Button
								variant='large'
								fitContent
								onClick={fetchData}
								disabled={(!startDate || !endDate) && (!startPaymentDate || !endPaymentDate) && !placa}
							>
								Gerar
							</Button>
						</HalfLineButton>
					</FullLine>
				</HalfLineButton>
			</Form>
			{loading && <Loading />}
			{filteredOffers.length > 0 && (
				<ReportCard
					data={[
						{
							color: '#4CAF50',
							title: 'Valor Pagamentos Recebidos no Período',
							value: moneyFormatter(valorRecebido),
						},
						{
							color: '#2196F3',
							title: 'Qtde Pagamentos Recebidos no Período',
							value:  pagamentosRecebidos.length,
						},
						{
							color: '#f44336',
							title: 'Valor Pagamentos Pendentes no Período',
							value: moneyFormatter(valorPendente),
						},
						{
							color: '#FF9800',
							title: 'Qtde Pagamentos Pendentes no Período',
							value: pagamentosPendentes.length,
						}
					]}
				/>
			)}
			<TableContainer>
				{(loading || (filteredOffers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && filteredOffers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
				{graphLineData.length > 0 && (
					<FullLine>
						<Line
							data={{
								labels: graphLineData.map((item) => item.label),
								datasets: [
									{
										label: 'Qtde Pagamentos por período',
										data: graphLineData.map((item) => item.qtde),
										backgroundColor: 'rgba(255, 102, 0, 0.5)',
										borderColor: 'rgba(255, 102, 0)',
									},
								],
							}}
						/>
					</FullLine>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminPaymentsReport;
